import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Phone, Mail, MapPin, Clock, CheckCircle, Gift } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import { Button } from '@/components/ui/button';
import DynamicIcon from '@/components/common/DynamicIcon';
import { pmsFrom } from '@/integrations/pms';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { ContactForm } from '@/components/forms/ContactForm';
import { DynamicForm } from '@/components/common/DynamicForm';
import { usePageDesign } from '@/hooks/usePageDesign';

export const ContactPage: React.FC = () => {
  const { services, serviceAreas, template } = useSiteContent();
  const [searchParams] = useSearchParams();
  const hasPromo = searchParams.get('promo') === 'new-customer';
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('contact');

  const { data: biz } = useQuery({
    queryKey: ['business-settings-contact'],
    queryFn: async () => {
      const { data } = await pmsFrom('business_settings')
        .select('company_name, phone, email, address_street, address_city, address_state, address_zip, business_hours, trust_badges')
        .limit(1)
        .maybeSingle();
      return data;
    },
  });

  const phone = biz?.phone || template.contact?.phone || '';
  const email = biz?.email || template.contact?.email || '';
  const street = biz?.address_street || template.contact?.address || '';
  const city = biz?.address_city || template.contact?.city || '';
  const state = biz?.address_state || template.contact?.state || '';
  const zip = biz?.address_zip || template.contact?.zip || '';
  const hours = biz?.business_hours || template.contact?.hours || '';
  const companyName = biz?.company_name || 'Our Company';
  const trustBadges = (biz?.trust_badges as any[]) || template.trustBadges || [];
  
  const [isSubmitted, setIsSubmitted] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Promo Banner */}
      {hasPromo && (
        <section className="bg-gradient-to-r from-primary to-accent text-primary-foreground py-4 relative z-20">
          <div className="container mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-3">
              <Gift className="h-5 w-5" />
              <span className="font-semibold">🎉 New Customer Special: 15% Off Applied!</span>
            </div>
            <p className="text-sm text-primary-foreground/80 mt-1">
              Complete the form below to claim your discount
            </p>
          </div>
        </section>
      )}

      <ConfigurableHero
        heroStyle={heroStyle}
        title="Contact Us"
        subtitle="Ready to start your project? Get in touch with our team today. We respond within 24 hours."
        badge="Free Estimates"
        showBreadcrumbs={showBreadcrumbs}
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Contact' }]}
        showTrustBadges={showTrustBadges}
        showCtaButtons={showCtaButtons}
        ctaPrimaryText={`Call ${phone || 'Now'}`}
        ctaPrimaryHref={`tel:${phone.replace(/\D/g, '')}`}
        phone={phone}
      />

      {/* Contact Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-6">Get In Touch</h2>
              <p className="text-muted-foreground mb-8">
                Have a question or ready to start your project? Reach out using any of the methods below, 
                or fill out the form and we'll get back to you within 24 hours.
              </p>

              <div className="space-y-6 mb-8">
                {/* Phone */}
                <motion.a
                  href={`tel:${phone.replace(/\D/g, '')}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/50 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                    <Phone className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Call us now</p>
                    <p className="font-semibold text-lg">{phone}</p>
                  </div>
                </motion.a>

                {/* Email */}
                <motion.a
                  href={`mailto:${email}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/50 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors">
                    <Mail className="h-6 w-6 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email us</p>
                    <p className="font-semibold">{email}</p>
                  </div>
                </motion.a>

                {/* Address */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Visit us</p>
                    <p className="font-semibold">{street}</p>
                    <p className="text-muted-foreground">{city}, {state} {zip}</p>
                  </div>
                </motion.div>

                {/* Hours */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Business Hours</p>
                    <p className="font-semibold whitespace-pre-line">{hours}</p>
                  </div>
                </motion.div>
              </div>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4">
                {trustBadges.map((badge: any, index: number) => (
                  <div key={index} className="text-center p-4 bg-muted rounded-lg">
                    <DynamicIcon name={badge.icon} className="h-6 w-6 text-primary mx-auto mb-2" />
                    <p className="text-xs font-medium">{badge.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-card p-8 rounded-2xl border border-border"
            >
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-2">Message Sent!</h3>
                  {hasPromo && (
                    <p className="text-primary font-semibold mb-2 flex items-center justify-center gap-2">
                      <Gift className="h-4 w-4" />
                      Your 15% discount has been applied to your lead!
                    </p>
                  )}
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                  <Button onClick={() => setIsSubmitted(false)} variant="outline">
                    Send Another Message
                  </Button>
                </motion.div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading text-xl font-semibold">Send Us a Message</h3>
                    {hasPromo && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                        <Gift className="h-3 w-3" />
                        15% Off Applied
                      </div>
                    )}
                  </div>
                  <DynamicForm pageSlug="contact" className="" />
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-heading text-xl font-bold mb-4">Service Area Coverage</h3>
            <p className="text-muted-foreground mb-6">
              We proudly serve {city || 'your area'} and the surrounding {serviceAreas.length} communities.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {serviceAreas.slice(0, 8).map((area) => (
                <span key={area.name} className="px-3 py-1 bg-background rounded-full text-sm">
                  {area.name}
                </span>
              ))}
              {serviceAreas.length > 8 && (
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  +{serviceAreas.length - 8} more
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Scheduling Embed Placeholder */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl font-bold mb-4">Schedule Online</h2>
            <p className="text-muted-foreground mb-8">
              Prefer to schedule directly? Use our online booking system.
            </p>
            <div className="aspect-video bg-muted rounded-xl flex items-center justify-center border-2 border-dashed border-border">
              <div className="text-center text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4" />
                <p className="font-medium">Online Scheduling</p>
                <p className="text-sm">Embed placeholder for booking widget</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};
